import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── helpers ──────────────────────────────────────────────────────────────────

function getAccessToken(): string | null {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
}

function storeTokens(accessToken: string, refreshToken: string, rememberMe: boolean): void {
  const storage = rememberMe ? localStorage : sessionStorage;
  storage.setItem('token', accessToken);
  storage.setItem('refreshToken', refreshToken);
}

export function clearSession(): void {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('rememberMe');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
}

// ── request interceptor ──────────────────────────────────────────────────────

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── response interceptor – silent token refresh on 401 ──────────────────────

let isRefreshing = false;
let pendingRequests: Array<(token: string) => void> = [];

function onTokenRefreshed(newToken: string): void {
  pendingRequests.forEach(resolve => resolve(newToken));
  pendingRequests = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh for 401 errors that aren't the refresh call itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearSession();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until the refresh finishes
        return new Promise((resolve, reject) => {
          pendingRequests.push((token: string) => {
            if (!token) {
              reject(error);
              return;
            }
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
        const newAccess: string = data.accessToken;
        const newRefresh: string = data.refreshToken;
        const rememberMe = localStorage.getItem('rememberMe') === 'true';

        storeTokens(newAccess, newRefresh, rememberMe);
        api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
        onTokenRefreshed(newAccess);

        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return api(originalRequest);
      } catch {
        clearSession();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username: string, password: string, twoFactorCode?: string) =>
    api.post('/auth/login', { username, password, twoFactorCode }),
  logout: () => api.post('/auth/logout'),
  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data: any) => api.put('/auth/profile', data),
  getMyPermissions: () => api.get('/auth/my-permissions'),
  setup2FA: () => api.post('/auth/2fa/setup'),
  verify2FA: (code: string) => api.post('/auth/2fa/verify', { code }),
  disable2FA: (password: string, code: string) => api.post('/auth/2fa/disable', { password, code }),
  get2FAStatus: () => api.get('/auth/2fa/status'),
};

export const serverAPI = {
  getStatus: () => api.get('/server/status'),
  getOnlinePlayers: () => api.get('/server/players'),
  getGameModes: () => api.get('/server/gamemodes'),
  getPlayerCounts: () => api.get('/server/playercounts'),
};

export const storeAPI = {
  // Categories
  getCategories: () => api.get('/store/categories'),
  // Features
  getFeatures: () => api.get('/store/features'),
  // Products
  getProducts: (params?: { category?: string; categoryId?: number; featured?: boolean }) =>
    api.get('/store', { params }),
  getProductBySlug: (slug: string) => api.get(`/store/${slug}`),
  getProductsByCategory: (category: string) => api.get(`/store/category/${category}`),
  getFeaturedProducts: () => api.get('/store/featured'),
  // Ranks with comparison
  getRanksComparison: () => api.get('/store/ranks/compare'),
  // Coin packages
  getCoinPackages: () => api.get('/store/coins/packages'),
  // Rank purchase
  purchaseRank: (productSlug: string, durationMonths: number) =>
    api.post('/store/purchase/rank', { productSlug, durationMonths }),
  checkRankOwnership: (slug: string) => api.get(`/store/rank/check/${slug}`),
  // Orders
  createOrder: (items: any[]) => api.post('/store/order', { items }),
  getOrderHistory: () => api.get('/store/orders'),
  applyDiscount: (code: string) => api.post('/store/discount', { code }),
};

export const tebexAPI = {
  createCheckout: (items: Array<{ productId: string; quantity: number }>) =>
    api.post('/tebex/create-checkout', { items }),
  finalizeCheckout: (basketIdent: string) =>
    api.post('/tebex/finalize-checkout', { basketIdent }),
  getOrder: (orderNumber: string) =>
    api.get(`/tebex/order/${orderNumber}`),
};

export const blogAPI = {
  getPosts: (page?: number, limit?: number) =>
    api.get('/blog/posts', { params: { page, limit } }),
  getPost: (id: string) => api.get(`/blog/posts/${id}`),
  getFeatured: () => api.get('/blog/featured'),
};

export const teamAPI = {
  getMembers: () => api.get('/team/members'),
};

export const contactAPI = {
  submit: (data: { name: string; email: string; subject: string; message: string }) =>
    api.post('/contact', data),
};

export const faqAPI = {
  getQuestions: () => api.get('/faq'),
  getCategories: () => api.get('/faq/categories'),
};

export const staffAPI = {
  getMembers: () => api.get('/staff'),
  getMember: (id: number) => api.get(`/staff/${id}`),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getPermissions: () => api.get('/admin/permissions'),

  getFaqs: () => api.get('/admin/faqs'),
  createFaq: (data: { question: string; answer: string; category: string; order?: number; isActive?: boolean }) =>
    api.post('/admin/faqs', data),
  updateFaq: (id: number, data: { question?: string; answer?: string; category?: string; order?: number; isActive?: boolean }) =>
    api.put(`/admin/faqs/${id}`, data),
  deleteFaq: (id: number) => api.delete(`/admin/faqs/${id}`),
  reorderFaqs: (items: { id: number; order: number }[]) =>
    api.put('/admin/faqs/reorder', { items }),

  getStaff: () => api.get('/admin/staff'),
  createStaff: (data: any) => api.post('/admin/staff', data),
  updateStaff: (id: number, data: any) => api.put(`/admin/staff/${id}`, data),
  deleteStaff: (id: number) => api.delete(`/admin/staff/${id}`),

  getBlogPosts: () => api.get('/admin/blog'),
  createBlogPost: (data: any) => api.post('/admin/blog', data),
  updateBlogPost: (id: number, data: any) => api.put(`/admin/blog/${id}`, data),
  deleteBlogPost: (id: number) => api.delete(`/admin/blog/${id}`),
  publishBlogPost: (id: number, isPublished: boolean) =>
    api.put(`/admin/blog/${id}/publish`, { isPublished }),

  getProducts: () => api.get('/admin/products'),
  createProduct: (data: any) => api.post('/admin/products', data),
  updateProduct: (id: number, data: any) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id: number) => api.delete(`/admin/products/${id}`),

  getUsers: (page?: number, limit?: number, search?: string) =>
    api.get('/admin/users', { params: { page, limit, search } }),
  getUser: (id: number) => api.get(`/admin/users/${id}`),
  updateUser: (id: number, data: any) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id: number) => api.delete(`/admin/users/${id}`),
  updateUserRoles: (id: number, roleIds: number[]) =>
    api.put(`/admin/users/${id}/roles`, { roleIds }),
  updateUserPermissions: (id: number, permissionIds: number[]) =>
    api.put(`/admin/users/${id}/permissions`, { permissionIds }),

  getRoles: () => api.get('/admin/roles'),
  createRole: (data: any) => api.post('/admin/roles', data),
  updateRole: (id: number, data: any) => api.put(`/admin/roles/${id}`, data),
  deleteRole: (id: number) => api.delete(`/admin/roles/${id}`),

  getAllPermissions: () => api.get('/admin/permissions/all'),

  getCategories: () => api.get('/admin/categories'),
  createCategory: (data: any) => api.post('/admin/categories', data),
  updateCategory: (id: number, data: any) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id: number) => api.delete(`/admin/categories/${id}`),

  getFeatures: () => api.get('/admin/features'),
  createFeature: (data: any) => api.post('/admin/features', data),
  updateFeature: (id: number, data: any) => api.put(`/admin/features/${id}`, data),
  deleteFeature: (id: number) => api.delete(`/admin/features/${id}`),

  getServers: () => api.get('/admin/servers'),
  createServer: (data: any) => api.post('/admin/servers', data),
  updateServer: (id: number, data: any) => api.put(`/admin/servers/${id}`, data),
  deleteServer: (id: number) => api.delete(`/admin/servers/${id}`),
};

export const coinAPI = {
  getBalance: () => api.get('/coin/balance'),
  getPlayerBalance: (playerUuid: string) => api.get(`/coin/balance/${playerUuid}`),
  getHistory: (page?: number, limit?: number) =>
    api.get('/coin/history', { params: { page, limit } }),
  getLeaderboard: (limit?: number) => api.get('/coin/leaderboard', { params: { limit } }),
  transfer: (toPlayerUuid: string, amount: number, reason?: string) =>
    api.post('/coin/transfer', { toPlayerUuid, amount, reason }),
  purchase: (amount: number, paymentReference: string) =>
    api.post('/coin/purchase', { amount, paymentReference }),
  spend: (amount: number, itemId: string, itemName: string) =>
    api.post('/coin/spend', { amount, itemId, itemName }),
  linkMinecraft: (minecraftUUID: string, minecraftUsername: string) =>
    api.post('/coin/link-minecraft', { minecraftUUID, minecraftUsername }),
  adminGive: (playerUuid: string, amount: number, reason?: string) =>
    api.post('/coin/admin/give', { playerUuid, amount, reason }),
  adminTake: (playerUuid: string, amount: number, reason?: string) =>
    api.post('/coin/admin/take', { playerUuid, amount, reason }),
  adminSet: (playerUuid: string, amount: number, reason?: string) =>
    api.post('/coin/admin/set', { playerUuid, amount, reason }),
};

export const rankAPI = {
  getMyRank: () => api.get('/rank/my-rank'),
  getPlayerRank: (username: string) => api.get(`/rank/player/${username}`),
  getAllRanks: () => api.get('/rank/all'),
};

export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  getStats: () => api.get('/users/stats'),
  getPurchases: (page?: number, limit?: number) =>
    api.get('/users/purchases', { params: { page, limit } }),
};

export default api;
